const userId = import.meta.env.VITE_OASIS_USER_ID;

// React
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// Componentes
import MiniLoadingSpinner from "../../components/MiniLoadingSpinner";
// import CustomInputFile from "../../components/CustomInputFile";
import { CustomAlert } from "../../components/CustomAlert";
import CustomButton from "../../components/CustomButton";
import SimpleTable from "../../components/SimpleTable";
import EditButton from "../../components/EditButton";

// Iconos
// import ExcelTableIcon from "../../icons/ExcelTableIcon";
import MoreInfoIcon from "../../icons/MoreInfoIcon";
// import ExcelTemplateIcon from "../../icons/ExcelTemplateIcon";

// Funciones
import { fetchYards, useYardStore } from "../../../../store/oasis/yardsStore";
import { resetAlert } from "../../helpers";
import { yardsTableColumns, onDelete } from "./functions";
import type { Yard } from "../form/functions";
import type { SweetAlertIcon } from "sweetalert2";

// Estilos
import {
  ListContainer,
  NavbarListContainer,
  Container1,
  Path,
  NavbarTitle,
  // ExcelButtonsContainer,
  ButtonsContainer,
  TableContainer,
  CheckboxAllMsjContainer,
  CheckboxAllMsj,
  CheckboxContainer,
} from "./styles";

// Contexto
import { useDarkMode } from "../../../../context/ThemeContext";

function YardsList() {
  // Hooks
  const { t } = useTranslation("yardsList");
  const navigate = useNavigate();

  // Modo oscuro
  const { isDarkMode } = useDarkMode();

  // Estados globales
  const { setYardSelected, setIsEdit } = useYardStore();

  // Estados locales mínimos para maquetado
  // const [firstLoad, setFirstLoad] = useState(false);
  const [alert, setAlert] = useState<{
    visible: boolean;
    title?: string;
    text?: string;
    icon?: SweetAlertIcon;
    onConfirm?: () => void;
    confirmButtonText?: string;
    confirmButtonColor?: string;
    showCancelButton?: boolean;
    cancelButtonText?: string;
    cancelButtonColor?: string;
    onCancel?: () => void;
  }>({ visible: false });
  // const [showModal, setShowModal] = useState(false);
  const [isAnyChecked, setIsAnyChecked] = useState(false);
  // const [fileUploaded, setFileUploaded] = useState(false);
  const [dataFile, setDataFile] = useState([]);
  // const [sendingFile, setSendingFile] = useState(false);
  const [selectAll, setSelectAll] = useState(false);

  const [loading, setLoading] = useState(false);
  const [yards, setYards] = useState<Yard[]>([]);
  const [yardsDelete, setYardsDelete] = useState<{ id: string }[]>([]);

  const newYardsList = useMemo(
    () =>
      yards.map((item) => ({
        ...item,
        checkbox: () => {
          const handleCheckbox = (e: any) => {
            let updatedYardsArray;
            if (e.target.checked)
              updatedYardsArray = [...yardsDelete, { id: String(item.id) }];
            else
              updatedYardsArray = yardsDelete.filter(
                (yard) => yard.id !== item.id
              );

            // Filtrar y asegurar que id siempre sea string
            const filteredYardsArray = updatedYardsArray
              .filter(
                (yard: { id: string | undefined }) =>
                  typeof yard.id === "string"
              )
              .map((yard: { id: string | undefined }) => ({
                id: yard.id as string,
              }));
            setYardsDelete(filteredYardsArray);
            setIsAnyChecked(filteredYardsArray.length > 0);

            // Marca el checkbox "Seleccionar todo" si todos los checkboxes individuales están marcados
            setSelectAll(filteredYardsArray.length === yards.length);
          };
          return (
            <CheckboxContainer key={item.id}>
              <input
                type="checkbox"
                name="yardsDelete"
                value={item.id}
                disabled={loading}
                checked={yardsDelete.some((yard) => yard.id === item.id)}
                className={!loading ? "cursor-pointer" : "cursor-not-allowed"}
                onChange={handleCheckbox}
              />
            </CheckboxContainer>
          );
        },
        ID: () => <MoreInfoIcon colorIcon="#4d5254" msg={item.id ?? ""} />,
        edit: () => {
          const handleClick = () => {
            setIsEdit(true);
            navigate(`/oasis/yards/form`);
            setYardSelected(item);
          };
          return (
            <EditButton
              handleClick={handleClick}
              loading={loading}
              firstLoad={false}
              isAnyChecked={isAnyChecked}
              fileUploaded={false}
            />
          );
        },
      })),
    [yards, yardsDelete, isAnyChecked, loading, navigate, setIsEdit]
  );

  // Checkbox de seleccionar todo (solo visual)
  const selectAllCheckboxes = () => {
    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    return (
      <div>
        <input
          type="checkbox"
          checked={selectAll}
          className={
            yards.length !== 0 || loading
              ? "cursor-pointer"
              : "cursor-not-allowed"
          }
          disabled={yards.length === 0 || loading}
          onChange={(e) => {
            setSelectAll(e.target.checked);
            const updatedYards = e.target.checked
              ? yards.map((yard) => ({ id: yard.id }))
              : [];

            const filteredYardsArray = updatedYards
              .filter(
                (yard: { id: string | undefined }) =>
                  typeof yard.id === "string"
              )
              .map((yard: { id: string | undefined }) => ({
                id: yard.id as string,
              }));
            setYardsDelete(filteredYardsArray);
            setIsAnyChecked(filteredYardsArray.length > 0);
          }}
          onMouseEnter={() => setIsTooltipVisible(true)}
          onMouseLeave={() => setIsTooltipVisible(false)}
        />
        {isTooltipVisible && (
          <CheckboxAllMsjContainer>
            <CheckboxAllMsj>{t("checkboxAllMsj")}</CheckboxAllMsj>
          </CheckboxAllMsjContainer>
        )}
      </div>
    );
  };

  const handleDeleteYards = () => {
    setAlert({
      visible: true,
      title: t("areYouSure"),
      text: yardsDelete.length > 1 ? t("removeYards") : t("removeYard"),
      icon: "warning",
      confirmButtonText: t("deleteButton"),
      confirmButtonColor: "#df4040",
      onConfirm: () => {
        setAlert(resetAlert());
        const yardsData = {
          userId,
          yardIDs: yardsDelete.map((yard) => yard.id),
        };
        // Lógica para eliminar los patios seleccionados
        return onDelete(
          yardsData,
          setAlert,
          t,
          setIsAnyChecked,
          setSelectAll,
          setYardsDelete,
          setLoading,
          setYards
        );
      },
      showCancelButton: true,
      cancelButtonText: t("cancelButton"),
      cancelButtonColor: "#1C93FF",
      onCancel: () => setAlert(resetAlert()),
    });
  };

  // Maneja la creación o eliminación de patio(s)
  const handleButtonClick = () => {
    isAnyChecked ? handleDeleteYards() : navigate(`/oasis/yards/form`);
  };

  // EFECTOS

  // Cargar patios
  useEffect(() => {
    setLoading(true);
    fetchYards()
      .then((data) => {
        setYards(data);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <ListContainer
      className={isDarkMode ? "text-white" : "bg-white text-gray-900"}
    >
      {/* {(firstLoad || loading) && <LoadingSpinner />} */}
      {alert.visible && (
        <CustomAlert
          title={alert.title || ""}
          text={alert.text || ""}
          icon={alert.icon ?? "error"}
          confirmButtonText={alert.confirmButtonText}
          confirmButtonColor={alert.confirmButtonColor}
          onConfirm={alert.onConfirm}
          showCancelButton={alert.showCancelButton}
          cancelButtonText={alert.cancelButtonText}
          cancelButtonColor={alert.cancelButtonColor}
          onCancel={alert.onCancel}
        />
      )}
      {/* {showModal && (
        <ExcelFileStatusModal
          setShowModal={setShowModal}
          closeModal={() => setShowModal(false)}
          excelFileResponse={() => []}
          loading={false}
          responseTableColumns={[]}
          t={t}
        />
      )} */}
      {/* -------------------------------- Navbar -------------------------------- */}
      <NavbarListContainer
        className={isDarkMode ? "bg-[#232323]" : "bg-white "}
        id="navbar-yards"
      >
        <Container1>
          <Path className={isDarkMode ? "text-gray-400" : "text-gray-400"}>
            {t("truckYards")}
          </Path>
          <NavbarTitle
            className={isDarkMode ? "text-[#1C93FF]" : "text-[#0052A2]"}
          >
            {t("truckYards")}
          </NavbarTitle>
          {/*
          <ExcelButtonsContainer>
            {yards.length > 0 && (
              <button
                onClick={() => {}}
                disabled={isAnyChecked}
                title={t("downloadTable")}
              >
                <ExcelTableIcon
                  loading={sendingFile}
                  isAnyChecked={isAnyChecked}
                />
              </button>
            )}
            <button
              id="yardExcel"
              title={t("downloadExcelTemplate")}
              onClick={() => {}}
            >
              <ExcelTemplateIcon />
            </button> 
          </ExcelButtonsContainer>
          */}
        </Container1>
        <Container1>
          <ButtonsContainer>
            {loading && <MiniLoadingSpinner />}
            {/* <CustomInputFile
              id="loadButton"
              disable={isAnyChecked}
              uploaded={fileUploaded}
              handleFileChange={() => {}}
              fileDelete={() => {
                setDataFile([]);
                setFileUploaded(false);
              }}
              text={
                sendingFile
                  ? t("sendingFile")
                  : fileUploaded
                  ? t("uploadedFile")
                  : t("fileUpload")
              }
            /> */}
            {dataFile.length > 0 ? (
              <CustomButton
                disable={false}
                handleClick={() => {}}
                styleRendering={
                  isDarkMode
                    ? "bg-[#1C93FF] hover:bg-[#197CD6] text-white"
                    : "bg-[#1C93FF] hover:bg-[#197CD6] text-white"
                }
                text={t("sendFile")}
              />
            ) : (
              <CustomButton
                id="create-new-yard-btn"
                disable={loading}
                handleClick={handleButtonClick}
                styleRendering={
                  loading
                    ? "bg-[#1c91ff87] cursor-not-allowed"
                    : isAnyChecked
                    ? "bg-[#df4040] hover:bg-[#893333] text-white"
                    : isDarkMode
                    ? "bg-[#0052A2] hover:bg-[#004080] text-white"
                    : "bg-[#1C93FF] hover:bg-[#197CD6] text-white"
                }
                text={isAnyChecked ? t("delete") : t("createNew")}
              />
            )}
            {/* <TutorialButton
              handleClick={() => {}}
              text={"Tutorial"}
              styleRendering="bg-[#1C93FF] hover:bg-[#197CD6]"
            /> */}
          </ButtonsContainer>
        </Container1>
      </NavbarListContainer>
      {/* -------------------------------- Tabla -------------------------------- */}
      <TableContainer
        className={isDarkMode ? "bg-dark-surface rounded-xl" : "bg-white"}
        id="yards-table-container"
      >
        <div className="w-full rounded-xl overflow-hidden" id="yards-table">
          <SimpleTable
            data={newYardsList}
            tableColumns={() => yardsTableColumns(t, selectAllCheckboxes)}
            t={t}
            defaultText={t("NoTruckYards")}
            action1={selectAllCheckboxes}
            disabled={false}
            isDarkMode={isDarkMode}
          />
        </div>
      </TableContainer>
    </ListContainer>
  );
}

export default YardsList;
